import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CreditCard } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { useAuth } from "../../context/AuthContext";
import { AUTHORIZATION_TEXT } from "../../data/membership";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "bg-[#0a0a0a] border-zinc-800 text-white h-11 rounded-sm placeholder:text-zinc-600";

const Field = ({ label, id, value, onChange, required, type = "text", placeholder }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-zinc-300 text-sm">{label}{required && " *"}</Label>
    <Input
      id={id}
      data-testid={`enroll-input-${id}`}
      type={type}
      required={required}
      className={inputCls}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(id, e.target.value)}
    />
  </div>
);

const REFERRAL_OPTIONS = ["Google Search", "Facebook", "Friend / Family Referral", "Repeat Customer", "Drove By / Saw Our Vehicle", "Other"];

export const EnrollForm = () => {
  const { user } = useAuth();
  const isLoggedIn = Boolean(user);
  const [form, setForm] = useState({
    first_name: user?.first_name || "", last_name: user?.last_name || "",
    email: user?.email || "", phone: user?.phone || "", password: "",
    street: "", city: "Las Vegas", state: "NV", zip: "",
    preferred_communication: "Text", emergency_contact: "", referral_source: "",
    vehicle_year: "", vehicle_make: "", vehicle_model: "",
  });
  const [smsOptIn, setSmsOptIn] = useState(true);
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [billingAuthorized, setBillingAuthorized] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (id, v) => setForm((f) => ({ ...f, [id]: v }));
  const canSubmit = billingAuthorized && termsAgreed && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/membership/enroll`, {
        origin_url: window.location.origin,
        ...form,
        password: isLoggedIn ? undefined : form.password,
        sms_opt_in: smsOptIn,
        email_opt_in: emailOptIn,
        billing_authorized: billingAuthorized,
        terms_agreed: termsAgreed,
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "We couldn't process your enrollment. Please review your information or try again.");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} id="enroll" className="bg-[#121212] border border-blue-500/30 rounded-xl p-8 sm:p-10 shadow-[0_0_40px_-12px_rgba(37,99,235,0.25)] scroll-mt-28" data-testid="enroll-form">
      <div className="flex items-baseline justify-between mb-8">
        <span className="font-heading text-lg font-semibold text-white">Join the Membership</span>
        <span className="font-heading text-3xl font-bold text-blue-500">$39.99<span className="text-sm text-zinc-500">/mo</span></span>
      </div>

      <div className="space-y-6">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Your Information</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="First Name" id="first_name" value={form.first_name} onChange={set} required />
            <Field label="Last Name" id="last_name" value={form.last_name} onChange={set} required />
            <Field label="Email" id="email" type="email" value={form.email} onChange={set} required />
            <Field label="Mobile Phone" id="phone" type="tel" value={form.phone} onChange={set} required placeholder="(702) 555-0123" />
          </div>
          {!isLoggedIn && (
            <div className="mt-4">
              <Field label="Create Portal Password (8+ characters)" id="password" type="password" value={form.password} onChange={set} required placeholder="For your member portal account" />
            </div>
          )}
        </div>

        <div>
          <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Service Address</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Street Address" id="street" value={form.street} onChange={set} />
            <Field label="City" id="city" value={form.city} onChange={set} />
            <Field label="State" id="state" value={form.state} onChange={set} />
            <Field label="ZIP" id="zip" value={form.zip} onChange={set} required />
          </div>
        </div>

        <div>
          <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Vehicle (optional)</h4>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Year" id="vehicle_year" value={form.vehicle_year} onChange={set} />
            <Field label="Make" id="vehicle_make" value={form.vehicle_make} onChange={set} />
            <Field label="Model" id="vehicle_model" value={form.vehicle_model} onChange={set} />
          </div>
        </div>

        <div>
          <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Preferences</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-zinc-300 text-sm">Preferred Communication</Label>
              <select
                data-testid="enroll-select-communication"
                className="w-full h-11 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 text-sm"
                value={form.preferred_communication}
                onChange={(e) => set("preferred_communication", e.target.value)}
              >
                <option>Text</option><option>Phone Call</option><option>Email</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-300 text-sm">How did you hear about us?</Label>
              <select
                data-testid="enroll-select-referral"
                className="w-full h-11 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 text-sm"
                value={form.referral_source}
                onChange={(e) => set("referral_source", e.target.value)}
              >
                <option value="">Select...</option>
                {REFERRAL_OPTIONS.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4">
            <Field label="Emergency Contact (optional)" id="emergency_contact" value={form.emergency_contact} onChange={set} placeholder="Name & phone" />
          </div>
          <div className="flex flex-wrap gap-6 mt-4">
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <Checkbox data-testid="enroll-sms-optin" checked={smsOptIn} onCheckedChange={setSmsOptIn} /> SMS updates &amp; reminders
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <Checkbox data-testid="enroll-email-optin" checked={emailOptIn} onCheckedChange={setEmailOptIn} /> Email updates &amp; reminders
            </label>
          </div>
        </div>

        <div className="space-y-4 border-t border-zinc-800 pt-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="enroll-billing-authorization"
              checked={billingAuthorized}
              onCheckedChange={setBillingAuthorized}
              className="mt-0.5"
            />
            <span className="text-xs text-zinc-400 leading-relaxed">{AUTHORIZATION_TEXT}</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="enroll-terms-agreement"
              checked={termsAgreed}
              onCheckedChange={setTermsAgreed}
              className="mt-0.5"
            />
            <span className="text-xs text-zinc-400 leading-relaxed">
              I have reviewed and agree to the{" "}
              <Link to="/membership-agreement" target="_blank" className="text-blue-400 underline">Membership Terms</Link>,{" "}
              <Link to="/cancellation-policy" target="_blank" className="text-blue-400 underline">Cancellation Policy</Link>,{" "}
              <Link to="/terms" target="_blank" className="text-blue-400 underline">Terms of Service</Link>, and recurring billing authorization.
            </span>
          </label>
        </div>

        <button
          data-testid="enroll-submit-button"
          type="submit"
          disabled={!canSubmit}
          className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-sm px-8 py-4 font-semibold transition-colors"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          {loading ? "Redirecting to secure checkout..." : "Start My Membership — $39.99/Month"}
        </button>
        <p className="text-zinc-600 text-xs text-center">Secure payment processing provided by Stripe.</p>
      </div>
    </form>
  );
};
